import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import type { UwidFormatT } from "components/well-list/context";

/**
 * Projections published by context bridges — `null`-rendering components
 * mounted inside `ProjectProvider`/`WellListProvider` so the tour can read
 * context values Redux and the other stores cannot see. All fields optional:
 * `ctx` is empty until the relevant provider is mounted, and empty again
 * once a bridge unmounts or the tour stops running.
 */
export interface TourContextSnapshot {
  /** WellListTourBridge (WellListContext). */
  wellListUwidFormat?: UwidFormatT;
  /** ProjectTourBridge (ProjectContext). */
  selectedProjectId?: string;
}

interface TourBridgeState {
  ctx: TourContextSnapshot;
}

interface TourBridgeActions {
  /** Merges a bridge's projection into the shared snapshot. */
  publish: (patch: Partial<TourContextSnapshot>) => void;
  /** Removes previously-published keys, e.g. when a bridge unmounts. */
  clear: (keys: (keyof TourContextSnapshot)[]) => void;
  reset: () => void;
}

type TourBridgeStore = TourBridgeState & TourBridgeActions;

const initialState: TourBridgeState = { ctx: {} };

export const useTourBridgeStore = create<TourBridgeStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    /**
     * Skips the write when nothing changed. Bridges publish from effects whose
     * providers re-render often, and a fresh `ctx` object notifies every
     * subscriber — each of which then compares the snapshot — for a patch that
     * frequently carries the values already in it.
     */
    publish: (patch) =>
      set((state) => {
        // `patch` is a Partial<TourContextSnapshot>, so its keys are keyof by
        // construction; `Object.keys` is typed `string[]` and cannot say so.
        // This buys no checking while the repo runs `strict: false` — with
        // `noImplicitAny` off, TypeScript checks no index access at all — but
        // it types `key` for the reader and is already correct if strictness
        // is ever raised.
        const keys = Object.keys(patch) as (keyof TourContextSnapshot)[];
        const changed = keys.some((key) => state.ctx[key] !== patch[key]);
        return changed ? { ctx: { ...state.ctx, ...patch } } : state;
      }),

    clear: (keys) =>
      set((state) => {
        const ctx = { ...state.ctx };
        keys.forEach((key) => delete ctx[key]);
        return { ctx };
      }),

    reset: () => set(initialState)
  }))
);

export const selectTourContext = (s: TourBridgeStore): TourContextSnapshot => s.ctx;
export const selectPublish = (s: TourBridgeStore): TourBridgeActions["publish"] =>
  s.publish;
export const selectClear = (s: TourBridgeStore): TourBridgeActions["clear"] => s.clear;
